import React, { useState, useEffect } from 'react';
import { Link, useLocation } from 'react-router-dom';
import { motion, AnimatePresence } from 'framer-motion';
import * as FiIcons from 'react-icons/fi';
import SafeIcon from '../common/SafeIcon';

export default function Navbar() {
  const [isScrolled, setIsScrolled] = useState(false);
  const [isMobileMenuOpen, setIsMobileMenuOpen] = useState(false);
  const [isServicesOpen, setIsServicesOpen] = useState(false);
  const location = useLocation();

  useEffect(() => {
    const handleScroll = () => setIsScrolled(window.scrollY > 20);
    window.addEventListener('scroll', handleScroll);
    return () => window.removeEventListener('scroll', handleScroll);
  }, []);

  useEffect(() => {
    setIsMobileMenuOpen(false);
    setIsServicesOpen(false);
    window.scrollTo(0, 0);
  }, [location.pathname]);

  const services = [
    { name: 'Business Commercial', path: '/services/business-commercial' },
    { name: 'General Liability', path: '/services/general-liability' },
    { name: 'Workers Compensation', path: '/services/workers-compensation' },
    { name: 'Business Owner Policy', path: '/services/business-owner-policy' },
  ];

  return (
    <motion.nav 
      initial={{ y: -100 }}
      animate={{ y: 0 }}
      transition={{ duration: 0.6, ease: [0.22, 1, 0.36, 1] }}
      className={`fixed w-full z-50 transition-all duration-500 border-b ${isScrolled ? 'bg-white/90 backdrop-blur-xl border-zinc-200 shadow-sm py-3' : 'bg-white/50 backdrop-blur-md border-transparent py-5'}`}
    >
      <div className="max-w-7xl mx-auto px-4 sm:px-6 lg:px-8">
        <div className="flex justify-between items-center">
          <Link to="/" className="flex items-center gap-2 group">
            <motion.div whileHover={{ rotate: 180 }} transition={{ duration: 0.6 }}>
              <SafeIcon icon={FiIcons.FiShield} className="text-3xl text-red-600 drop-shadow-md" />
            </motion.div>
            <span className="font-extrabold text-2xl tracking-tighter text-zinc-900">
              Insurance<span className="text-red-600">Secrets</span>
            </span>
          </Link>

          {/* Desktop Nav */}
          <div className="hidden md:flex items-center space-x-8">
            <Link to="/" className="text-zinc-600 hover:text-red-600 font-semibold transition-colors">Home</Link>
            
            <div className="relative group">
              <button 
                className="flex items-center gap-1 text-zinc-600 hover:text-red-600 font-semibold transition-colors py-2"
                onMouseEnter={() => setIsServicesOpen(true)}
                onMouseLeave={() => setIsServicesOpen(false)}
              >
                Services <SafeIcon icon={FiIcons.FiChevronDown} className={`transition-transform duration-300 ${isServicesOpen ? 'rotate-180' : ''}`} />
              </button>
              
              <AnimatePresence>
                {isServicesOpen && (
                  <motion.div 
                    initial={{ opacity: 0, y: 10, scale: 0.95 }}
                    animate={{ opacity: 1, y: 0, scale: 1 }}
                    exit={{ opacity: 0, y: 10, scale: 0.95 }}
                    transition={{ duration: 0.2 }}
                    className="absolute top-full left-0 w-64 bg-white/95 backdrop-blur-xl shadow-2xl rounded-xl overflow-hidden border border-zinc-100"
                    onMouseEnter={() => setIsServicesOpen(true)}
                    onMouseLeave={() => setIsServicesOpen(false)}
                  >
                    <div className="py-2">
                      {services.map(service => (
                        <Link key={service.path} to={service.path} className="block px-5 py-3 text-sm font-medium text-zinc-600 hover:bg-red-50 hover:text-red-600 transition-colors">
                          {service.name}
                        </Link>
                      ))}
                    </div>
                  </motion.div>
                )}
              </AnimatePresence>
            </div>

            <Link to="/about" className="text-zinc-600 hover:text-red-600 font-semibold transition-colors">About Us</Link>
            <Link to="/contact" className="relative overflow-hidden group bg-zinc-900 text-white px-6 py-2.5 rounded-full font-semibold transition-all hover:shadow-[0_0_20px_rgba(220,38,38,0.4)] hover:bg-red-600">
              <span className="relative z-10">Get a Quote</span>
            </Link>
          </div>

          {/* Mobile Menu Button */}
          <button className="md:hidden text-zinc-900" onClick={() => setIsMobileMenuOpen(!isMobileMenuOpen)}>
            <SafeIcon icon={isMobileMenuOpen ? FiIcons.FiX : FiIcons.FiMenu} className="text-3xl" />
          </button>
        </div>
      </div>

      {/* Mobile Nav */}
      <AnimatePresence>
        {isMobileMenuOpen && (
          <motion.div 
            initial={{ opacity: 0, height: 0 }}
            animate={{ opacity: 1, height: 'auto' }}
            exit={{ opacity: 0, height: 0 }}
            className="md:hidden bg-white/95 backdrop-blur-xl border-t border-zinc-100 absolute w-full left-0 shadow-2xl overflow-hidden"
          >
            <div className="px-4 pt-2 pb-6 space-y-1 flex flex-col">
              <Link to="/" className="block px-3 py-4 text-zinc-800 font-bold border-b border-zinc-100">Home</Link>
              <div className="px-3 py-4 font-bold text-zinc-800 border-b border-zinc-100">
                Services
                <div className="pl-4 mt-3 space-y-3">
                  {services.map(service => (
                    <Link key={service.path} to={service.path} className="block text-sm font-medium text-zinc-500">{service.name}</Link>
                  ))}
                </div>
              </div>
              <Link to="/about" className="block px-3 py-4 text-zinc-800 font-bold border-b border-zinc-100">About Us</Link>
              <Link to="/contact" className="block px-3 py-4 text-red-600 font-bold">Contact & Quotes</Link>
            </div>
          </motion.div>
        )}
      </AnimatePresence>
    </motion.nav>
  );
}